/**
 * packages/core/src/auth-config.ts
 *
 * Gencow Auth 설정 타입 및 defineAuth() 함수.
 * 사용자 앱의 gencow/auth.ts 에서 사용.
 *
 * shadcn 패턴: auth.ts는 사용자가 소유하고 직접 수정할 수 있는 파일.
 * defineAuth()는 타입 안전한 설정 헬퍼일 뿐, 런타임 로직은 server에서 처리.
 */

import type { GencowDefaultUserIdentity } from "./context-auth.js";
import type { RlsSessionContext, RlsSessionContextBase } from "./rls-context-contract.js";
import { RLS_IDENTITY_CONTEXT_KEY } from "./rls-context-contract.js";

// ─── Email Verification ──────────────────────────────────

export interface AuthHookContext {
  db?: unknown;
  request?: unknown;
}

export interface AuthUserLike {
  id?: string;
  email: string;
  name?: string | null;
  emailVerified?: boolean;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface AuthEmailVerification {
  /** 가입 시 인증 메일 자동 발송 (default: true) */
  sendOnSignUp?: boolean;
  /** 이메일 미인증 시 로그인 차단 (default: true) */
  requireEmailVerification?: boolean;
  /** 인증 완료 후 자동 로그인 (default: true) */
  autoSignInAfterVerification?: boolean;
  /** 인증 메일 발송 함수 — 사용자가 직접 구현 */
  sendVerificationEmail: (
    data: {
      user: AuthUserLike;
      url: string;
      token: string;
    },
    context?: AuthHookContext,
  ) => Promise<void>;
  /** 인증 직전 훅 — better-auth의 emailVerification.beforeEmailVerification으로 전달 */
  beforeEmailVerification?: (user: AuthUserLike, context?: AuthHookContext) => Promise<void>;
  /** 인증 완료 훅 — 환영 메일 등 idempotent 후처리에 사용 */
  afterEmailVerification?: (user: AuthUserLike, context?: AuthHookContext) => Promise<void>;
}

export interface AuthPasswordReset {
  /** 비밀번호 재설정 메일 발송 함수 — reset URL은 반드시 사용자에게 전달되어야 한다. */
  sendResetPassword: (
    data: {
      user: AuthUserLike;
      url: string;
      token: string;
    },
    context?: AuthHookContext,
  ) => Promise<void>;
  /** 비밀번호 재설정 완료 후 훅 */
  onPasswordReset?: (data: { user: AuthUserLike }, context?: AuthHookContext) => Promise<void>;
  /** reset token 유효 시간(초). default: better-auth 기본값 3600초 */
  resetPasswordTokenExpiresIn?: number;
}

export interface AuthEvents {
  /** 회원가입 성공 후 실행. 운영 알림처럼 실패해도 가입을 막지 않아야 하는 작업에 사용. */
  afterSignUp?: (
    data: { user: AuthUserLike; inviteCode?: string | null },
    context?: AuthHookContext,
  ) => Promise<void>;
}

// ─── Custom User Fields ─────────────────────────────────

export type AuthUserFieldType = "text" | "string" | "boolean" | "integer" | "number" | "timestamp";

export interface AuthUserField {
  /** Scalar field type. `text` is emitted as Drizzle `text()` and better-auth `string`. */
  type: AuthUserFieldType;
  /** Optional database column name. Defaults to snake_case(fieldName). */
  columnName?: string;
  /** Whether the DB column may be null. Defaults to false when no default is provided. */
  nullable?: boolean;
  /** Default value for DB schema and better-auth runtime metadata. */
  default?: string | number | boolean;
  /** Whether signup/update input may set this field. Defaults to false for safety. */
  input?: boolean;
}

/** Runtime value type for one `user.additionalFields` entry. */
export type AuthUserFieldValue<F extends AuthUserField> = F["type"] extends "boolean"
  ? boolean
  : F["type"] extends "integer" | "number"
    ? number
    : string;

/** Maps `defineAuth({ user: { additionalFields } })` keys → `AppUser` fields (app-owned columns only). */
export type AppUserFromAdditionalFields<F extends Record<string, AuthUserField>> = {
  [K in keyof F & string]: AuthUserFieldValue<F[K]>;
};

/**
 * `ctx.auth.getUserIdentity()` shape: default identity + your extensions.
 * Use with `defineAuth<AppUser>(…)` so `mapSessionToContext` is typed against `AppUser`.
 *
 * Plugin fields (e.g. better-auth `admin()` → `role`) are not inferred from `betterAuth.plugins`;
 * intersect them from the `gencow/schema-auth` `user` row or declare manually.
 */
export type GencowAppUser<TExtra extends object = Record<string, never>> = GencowDefaultUserIdentity &
  TExtra;

/** Infers `AppUser` extras from `user.additionalFields` on a config object (not plugin fields). */
export type InferAppUserFromAuthConfigInput<T extends GencowAuthConfigInput> = GencowAppUser<
  T["user"] extends { additionalFields: infer F }
    ? F extends Record<string, AuthUserField>
      ? AppUserFromAdditionalFields<F>
      : Record<string, never>
    : Record<string, never>
>;

// ─── Social Login / OAuth ───────────────────────────────

export interface SocialProviderConfig {
  clientId: string;
  clientSecret: string;
  /** Provider-specific callback override. Most apps should leave this unset. */
  redirectURI?: string;
  /** Prevent provider login from creating a new user unless the request explicitly asks for signup. */
  disableImplicitSignUp?: boolean;
  /** Fully disable signup through this provider while preserving existing-account login/linking. */
  disableSignUp?: boolean;
}

export interface OAuthUserInfo {
  id: string;
  email: string;
  name?: string;
  image?: string;
  emailVerified?: boolean;
}

export interface CustomOAuthProvider {
  clientId: string;
  clientSecret?: string;
  authorizationUrl: string;
  tokenUrl: string;
  userInfoUrl: string;
  scopes?: string[];
  pkce?: boolean;
  /** Provider-specific callback override. Most apps should leave this unset. */
  redirectURI?: string;
  /** Map provider profile responses such as Naver/Kakao into better-auth user fields. */
  mapProfileToUser?: (
    profile: Record<string, unknown>,
  ) => Partial<OAuthUserInfo> | Promise<Partial<OAuthUserInfo>>;
}

export type KakaoConfig = SocialProviderConfig & Partial<CustomOAuthProvider>;
export type NaverConfig = SocialProviderConfig & Partial<CustomOAuthProvider>;

export type SocialProvidersConfig = {
  google?: SocialProviderConfig;
  apple?: SocialProviderConfig;
  /**
   * Kakao/Naver use Gencow's built-in OAuth endpoint defaults.
   * Override any CustomOAuthProvider field when a provider dashboard requires it.
   */
  kakao?: KakaoConfig;
  naver?: NaverConfig;
  /** Additional OAuth providers can be configured with explicit OAuth endpoints. */
  [providerId: string]: SocialProviderConfig | KakaoConfig | NaverConfig | CustomOAuthProvider | undefined;
};

// ─── RLS session mapping ─────────────────────────────────

/** RLS auth input (stored on `defineAuth` output as-is). */
export type RlsAuthConfigInput<TUser extends GencowDefaultUserIdentity = GencowDefaultUserIdentity> = {
  /**
   * Maps `AppUser` → transaction-local RLS context for `createRlsDb`.
   * When `user` is null, return the same keys with empty string values (fail-closed).
   * Sync only — must not return a Promise.
   */
  mapSessionToContext?: (
    input: { user: TUser | null },
  ) => RlsSessionContextBase & Record<string, string>;
};

/**
 * Default identity context key when `defineAuth` omits `rls` (`userId` → GUC `app.user_id`).
 * @see {@link RLS_IDENTITY_CONTEXT_KEY}
 */
export const DEFAULT_RLS_OWNER_SESSION_KEY = RLS_IDENTITY_CONTEXT_KEY;

export interface AuthOAuthConfig {
  /**
   * Frontend route that receives the short-lived OAuth handoff code.
   * If omitted, Gencow uses APP_PUBLIC_DOMAIN + /auth/callback, then falls back to the auth server origin.
   */
  callbackURL?: string;
  /** Extra allowed frontend callback URLs for local development or multi-frontend apps. */
  allowedCallbackURLs?: string[];
  /**
   * Server-enforced OAuth completion modes.
   *
   * Defaults to token-compatible behavior for existing apps. Configure `["cookie"]`
   * when OAuth completion must keep the Better Auth session token HttpOnly.
   */
  allowedReturnModes?: Array<"token" | "cookie">;
}

// ─── Auth Config ─────────────────────────────────────────

export interface GencowAuthBaseConfig {
  emailVerification?: AuthEmailVerification;
  socialProviders?: SocialProvidersConfig;
  oauth?: AuthOAuthConfig;
  passwordReset?: AuthPasswordReset;
  events?: AuthEvents;
  // 확장 예정:
  // passwordPolicy?: { ... }
  // sessionExpiry?: number
}

export interface GencowBetterAuthConfig extends GencowAuthBaseConfig {
  provider: "better-auth";
  user?: {
    /** App-specific scalar fields stored on the better-auth user table. */
    additionalFields?: Record<string, AuthUserField>;
  };
  /**
   * Maps `AppUser` → flat `RlsSessionContext` for `createRlsDb` / `ctx.db`.
   * See `docs/specs/spec-advanced-access-policies.md` §5.
   */
  rls?: RlsAuthConfigInput;
  /**
   * Escape hatch for provider-specific better-auth options/plugins.
   */
  betterAuth?: (defaults: Record<string, unknown>) => Record<string, unknown>;
}

export type GencowAuthConfig = GencowBetterAuthConfig;

export type GencowAuthConfigInput<TUser extends GencowDefaultUserIdentity = GencowDefaultUserIdentity> =
  Omit<GencowBetterAuthConfig, "provider" | "rls"> & {
    provider?: "better-auth";
    rls?: RlsAuthConfigInput<TUser>;
  };

/** Return type of {@link defineAuth}. */
export type GencowAuthConfigOutput<TUser extends GencowDefaultUserIdentity = GencowDefaultUserIdentity> =
  Omit<GencowAuthConfigInput<TUser>, "provider" | "rls"> & {
    provider: "better-auth";
    rls?: RlsAuthConfigInput<TUser>;
  };

// ─── defineAuth() ────────────────────────────────────────

/**
 * Auth 설정 정의 헬퍼.
 *
 * ## Row-level security (`rls`)
 *
 * **When `rls` is omitted** — the server maps `AppUser.id` → {@link RLS_IDENTITY_CONTEXT_KEY} (`userId`
 * → GUC `app.user_id`). {@link ownerRls} uses the same key by default.
 *
 * **When `rls.mapSessionToContext` is set** — return a flat context (sync); use empty strings when
 * `user` is null. **`userId` is required** on every return. Policy SQL uses {@link rlsContextKey} in schema.
 *
 * ```ts
 * export default defineAuth({
 *   rls: {
 *     mapSessionToContext({ user }) {
 *       if (!user) return { userId: "", role: "" };
 *       return { userId: user.id, role: String(user.role ?? "user") };
 *     },
 *   },
 * });
 * ```
 *
 * See `docs/specs/spec-advanced-access-policies.md` §5.
 *
 * @example
 * ```typescript
 * // gencow/auth.ts
 * import { defineAuth } from "gencow";
 *
 * export default defineAuth({
 *     emailVerification: {
 *         sendVerificationEmail: async ({ user, url }) => {
 *             // 이메일 발송 로직
 *         },
 *     },
 * });
 * ```
 */
export function defineAuth<TUser extends GencowDefaultUserIdentity = GencowDefaultUserIdentity>(
  config: GencowAuthConfigInput<TUser>,
): GencowAuthConfigOutput<TUser>;
export function defineAuth(config: GencowAuthConfigInput): GencowAuthConfigOutput;
export function defineAuth(config: GencowAuthConfigInput): GencowAuthConfigOutput {
  const provider = config.provider ?? "better-auth";
  return { ...config, provider, rls: config.rls };
}

export function hasMapSessionToContext<TUser extends GencowDefaultUserIdentity = GencowDefaultUserIdentity>(
  rls: RlsAuthConfigInput<TUser> | undefined,
): rls is RlsAuthConfigInput<TUser> & {
  mapSessionToContext: NonNullable<RlsAuthConfigInput<TUser>["mapSessionToContext"]>;
} {
  return !!rls && typeof rls === "object" && typeof rls.mapSessionToContext === "function";
}

export type { RlsSessionContext, RLS_IDENTITY_CONTEXT_KEY };
